/**
 * verify_email_controller.js — email-verification page behaviour (Stimulus).
 *
 * Scoped to the verify-email card. Handles the single interactive element: the
 * "resend verification email" button, which POSTs to auth.resend_verification and
 * reflects success/failure in the button. Replaces an end-of-body inline <script>
 * that used getElementById + addEventListener + DOMContentLoaded (none survive a
 * Turbo <body> swap) and called window.api (an authed-shell helper not present on
 * the public shell) — this uses a plain fetch, like login_controller's magic-link.
 *
 * Config is read from data-* attributes (SSOT): data-resend-url (endpoint),
 * data-email (the pending verification address from the session), and
 * data-csrf-token (the public shell carries no csrf-token meta and no fetch
 * wrapper, so this raw POST delivers the token itself). The shell now surfaces
 * flash() messages globally, so the old inline get_flashed_messages loop is gone.
 *
 * The POST asks for JSON back so a 429 from the resend rate limit arrives as
 * JSON with the limit's own message ("Links already sent..."),
 * shown in place of the generic failure, which would only invite another tap.
 * The Send gate prompt and the checklist button show the same message.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        resend(event) {
            var btn = event.currentTarget;
            var spinner = this.element.querySelector('#resendSpinner');
            var url = this.element.dataset.resendUrl;
            var email = this.element.dataset.email || '';
            var csrfToken = this.element.dataset.csrfToken || '';
            if (!url) return;

            btn.disabled = true;
            if (spinner) spinner.classList.remove('d-none');
            btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Sending...';

            // The endpoint returns rendered HTML and flashes the real outcome (the
            // shell surfaces flash() as a toast). Treat any 2xx as accepted and
            // reflect it on the button; only network/HTTP errors are failures.
            return fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
                body: 'email=' + encodeURIComponent(email) + '&csrf_token=' + encodeURIComponent(csrfToken),
                credentials: 'same-origin'
            })
                .then(function (r) {
                    if (r.ok) {
                        btn.innerHTML = '<i class="bi bi-check-circle me-2"></i>Email Sent!';
                        btn.classList.remove('btn-outline-primary');
                        btn.classList.add('btn-success');
                    } else {
                        btn.disabled = false;
                        if (spinner) spinner.classList.add('d-none');
                        btn.innerHTML = 'Resend Verification Email';
                        var fallback = 'Failed to send verification email. Please try again.';
                        var limited = r.status === 429
                            ? r.json().then(function (data) { return (data && data.message) || fallback; },
                                            function () { return fallback; })
                            : Promise.resolve(fallback);
                        return limited.then(function (message) {
                            if (window.showNotification) window.showNotification(message, 'error');
                        });
                    }
                })
                .catch(function () {
                    btn.disabled = false;
                    if (spinner) spinner.classList.add('d-none');
                    btn.innerHTML = 'Resend Verification Email';
                    if (window.showNotification) {
                        window.showNotification('An error occurred. Please try again.', 'error');
                    }
                });
        }
    }
